import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { getEventsByHostId, getHostById } from 'utility/requests';
import { Trans, t } from '@lingui/macro';
import * as GA4 from 'analytics/ga4';
import { Link } from 'react-router-dom';
import { setPageMetadata } from 'pageMetadata';

import MainAppBar from 'components/MainAppBar';
import EventListItem from 'components/EventListItem';
import HostPageSkeleton from 'components/HostPageSkeleton';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { cn } from 'lib/utils';

export default function HostPage() {
    const [state, setState] = useState({
        events: null,
        host: null,
        loading: true,
        error: false,
        success: false,
    });

    const params = useParams();

    // Reset scroll position to top after component renders
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const hostId = params.hostId.replace(/[^0-9]/g, '');

    let socialLinksAvailable = false;

    if (state.success) {
        const {
            url_website,
            url_facebook,
            url_twitter,
            url_instagram,
            url_tiktok,
            url_linkedin,
            url_youtube,
        } = state.host;
        socialLinksAvailable = Boolean(
            url_website ||
                url_facebook ||
                url_twitter ||
                url_instagram ||
                url_tiktok ||
                url_linkedin ||
                url_youtube,
        );
    }

    useEffect(() => {
        Promise.all([getEventsByHostId(hostId), getHostById(hostId)])
            .then(([events, host]) => {
                setState({
                    events: events,
                    host: host,
                    loading: false,
                    error: false,
                    success: true,
                });

                // Hardcode the path since users still get here from the old URL scheme where host id is passed in via query parameter on the root url. (Ex: https://frontdoor.plus/?o=123)
                GA4.pageView(`Host: ${host.host_name}`, `/host/${hostId}`);

                // Set HTML page title and description
                setPageMetadata({
                    title: `${host.host_name} Tickets`,
                    description: `Check out these upcoming events by ${host.host_name}`,
                    image: null,
                });
            })
            .catch((e) => {
                setState({
                    events: null,
                    host: null,
                    loading: false,
                    error: true,
                    success: false,
                });
            });
    }, [hostId]);

    return (
        <>
            <MainAppBar />

            {state.loading && <HostPageSkeleton />}

            {state.error && (
                <div className="flex min-h-[calc(100vh-160px)] w-full flex-col items-center justify-center px-4 text-center">
                    <h1 className="mb-6 text-2xl font-bold text-foreground">
                        <Trans>Sorry we couldn't find that organizer</Trans>
                    </h1>
                    <Button asChild variant="outline">
                        <Link to="/">
                            <Trans>View all events</Trans>
                        </Link>
                    </Button>
                </div>
            )}

            {state.success && (
                <div className="mx-auto w-full max-w-screen-xl px-4 pb-8 pt-4 sm:px-8">
                    <Card className="border-solid border-border shadow-none">
                        <CardContent className="flex flex-col items-center p-6">
                            <h1 className="mb-4 mt-0 text-center text-3xl font-bold text-foreground">
                                {state.host.host_name}
                            </h1>

                            {state.host.host_desc ? (
                                <p className="mb-8 mt-6 flex-1 text-center text-xl text-foreground">
                                    {state.host.host_desc}
                                </p>
                            ) : null}

                            {socialLinksAvailable ? <SocialLinks host={state.host} /> : null}
                        </CardContent>
                    </Card>
                </div>
            )}

            {state.success && (
                <div className="mx-auto w-full max-w-screen-xl px-4 pb-8 pt-4 sm:px-8">
                    {state.events.length === 0 && (
                        <p className="text-center text-foreground">
                            <Trans>There are no scheduled events for sale at the moment</Trans>
                        </p>
                    )}

                    {state.events.length > 0 && (
                        <h2 className="mb-4 text-2xl font-bold text-foreground">
                            <Trans>Upcoming Events</Trans>
                        </h2>
                    )}

                    <div className="grid gap-4 md:grid-cols-2">
                        {state.events.map((e) => (
                            <EventListItem key={e.event_id} event={e} />
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

function SocialLinks({ host }) {
    const iconBtn =
        'inline-flex cursor-pointer items-center justify-center h-10 w-10 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-colors';

    return (
        <div className="flex flex-wrap items-center justify-center gap-1">
            {host.url_website ? (
                <a
                    aria-label={t`Website`}
                    href={host.url_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={iconBtn}
                >
                    <Globe className="h-[22px] w-[22px]" strokeWidth={2} aria-hidden />
                </a>
            ) : null}
            {host.url_facebook ? (
                <a
                    aria-label="Facebook"
                    href={host.url_facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(iconBtn, 'hover:text-[#3b5998]')}
                >
                    <IconFacebook className="h-[22px] w-[22px]" />
                </a>
            ) : null}
            {host.url_instagram ? (
                <a
                    aria-label="Instagram"
                    href={host.url_instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(iconBtn, 'hover:text-[#E4405F]')}
                >
                    <IconInstagram className="h-[22px] w-[22px]" />
                </a>
            ) : null}
            {host.url_twitter ? (
                <a
                    aria-label="X"
                    href={host.url_twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={iconBtn}
                >
                    <img
                        alt=""
                        src={process.env.REACT_APP_STOREFRONT_S3_LOCATION + '/web/x-icon-black.svg'}
                        className="w-5 h-5 invert opacity-90"
                    />
                </a>
            ) : null}
            {host.url_tiktok ? (
                <a
                    aria-label="Tiktok"
                    href={host.url_tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={iconBtn}
                >
                    <img
                        alt=""
                        src={
                            process.env.REACT_APP_STOREFRONT_S3_LOCATION +
                            '/web/tiktok-icon-black.svg'
                        }
                        className="h-5 w-5 invert opacity-90"
                    />
                </a>
            ) : null}
            {host.url_linkedin ? (
                <a
                    aria-label="LinkedIn"
                    href={host.url_linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(iconBtn, 'hover:text-[rgb(51,117,178)]')}
                >
                    <IconLinkedIn className="h-[22px] w-[22px]" />
                </a>
            ) : null}
            {host.url_youtube ? (
                <a
                    aria-label="YouTube"
                    href={host.url_youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(iconBtn, 'hover:text-[rgb(219,56,38)]')}
                >
                    <IconYouTube className="h-[22px] w-[22px]" />
                </a>
            ) : null}
        </div>
    );
}

function IconFacebook({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
    );
}

function IconInstagram({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
    );
}

function IconLinkedIn({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
    );
}

function IconYouTube({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
    );
}
